import { z } from 'zod';
import { BusinessCodeSchema, EnteredNameSchema, NoteSchema, RecordIdSchema } from './identifiers';
import { DecimalStringSchema, MoneySchema } from './money';
import { HistoricalOriginSchema } from './origin';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Collections — `COL-*` demonstration slice (ADR-0025).
 *
 * Receipts, the allocation of money to installments, cheques and promissory notes, and an internal
 * reminder centre. **Not** a general ledger: there is no chart of accounts, no journal, and no
 * posting — those are Phase 6 and are blocked on `SD-08`. What exists here is the collection side of
 * a contract: money arrives, it is allocated, and a receipt records that it happened.
 *
 * The rule that shapes everything: **a posted receipt is never edited.** A mistake is corrected by
 * reversing the receipt and issuing a new one, because a receipt is a document the customer holds and
 * silently changing it makes the two copies disagree (ADR-0009).
 */

/* --------------------------------------------------------------------- receipt */

export const PAYMENT_METHODS = [
  'cash',
  'bankTransfer',
  'card',
  'cheque',
  'promissoryNote',
] as const;
export const PaymentMethodSchema = z.enum(PAYMENT_METHODS);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const RECEIPT_STATES = ['posted', 'reversed'] as const;
export const ReceiptStateSchema = z.enum(RECEIPT_STATES);
export type ReceiptState = z.infer<typeof ReceiptStateSchema>;

/** One installment this receipt paid, and by how much. The parts sum exactly to the receipt amount. */
export const AllocationSchema = z.strictObject({
  installmentId: RecordIdSchema,
  sequence: z.number().int().positive(),
  dueOn: BusinessDateSchema,
  amount: MoneySchema,
});
export type Allocation = z.infer<typeof AllocationSchema>;

export const ReceiptSchema = z.strictObject({
  receiptId: RecordIdSchema,
  /** Immutable once issued, unique, and printed on the document the customer keeps. */
  receiptNumber: BusinessCodeSchema,
  customerId: RecordIdSchema,
  /**
   * The contract the money settles. Absent only on a reservation deposit not yet applied: it is set
   * once, when the reservation's contract is activated and the deposit is allocated to its rows.
   */
  contractId: RecordIdSchema.optional(),
  /** A reservation deposit: money received on a reservation, before any contract existed. */
  reservationId: RecordIdSchema.optional(),
  /** When a reservation deposit was allocated to its contract's instalments. */
  appliedToContractAt: InstantSchema.optional(),
  projectId: RecordIdSchema,
  amount: MoneySchema,
  method: PaymentMethodSchema,
  /**
   * The bank account or cash box the money landed in. A **reference only**: ALOLA ERP never executes
   * a bank transfer and holds no bank credentials (MASTER-MAPPING §15). Treasury arrives in Phase 6.
   */
  depositReference: z.string().trim().max(120).optional(),
  /** The provider's or bank's own reference for the transaction, as given. Never reformatted. */
  transactionReference: z.string().trim().max(120).optional(),
  /** Set when the money arrived as a cheque or a promissory note that later cleared. */
  instrumentId: RecordIdSchema.optional(),
  /** The treasury account (bank or cashbox) the money landed in, when one was named (FIN-CASH-002). */
  treasuryAccountId: RecordIdSchema.optional(),
  /** May be empty only when the whole receipt is held as credit (`BD-38`). */
  allocations: z.array(AllocationSchema).max(240),
  /** The part held as the customer's unapplied credit, when `collections.overpaymentHandling` allows it. */
  unappliedAmount: MoneySchema.optional(),
  receivedByAccountId: z.string().min(1).max(200),
  receivedOn: BusinessDateSchema,
  state: ReceiptStateSchema,
  reversedAt: InstantSchema.optional(),
  reversedByAccountId: z.string().min(1).max(200).optional(),
  reversalReason: z.string().max(500).optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  teamId: RecordIdSchema.optional(),
  notes: NoteSchema.optional(),
  /**
   * Set on a receipt onboarded from the client's records (BD-63): money received before the cutover,
   * already inside the opening balances, so it moved no treasury account and posted nothing.
   */
  origin: HistoricalOriginSchema.optional(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Receipt = z.infer<typeof ReceiptSchema>;

/**
 * Record a collection.
 *
 * The caller may name the installments to pay, or leave `allocations` out and let the service settle
 * the oldest open rows first. Oldest-first is what a collections officer means by "put this against
 * his account", and it is deterministic, which matters when the customer asks why.
 */
export const RecordReceiptSchema = z.strictObject({
  contractId: RecordIdSchema,
  amount: MoneySchema,
  method: PaymentMethodSchema,
  receivedOn: BusinessDateSchema,
  depositReference: z.string().trim().max(120).optional(),
  transactionReference: z.string().trim().max(120).optional(),
  instrumentId: RecordIdSchema.optional(),
  /**
   * The treasury account the money landed in. Its book balance rises with the receipt and the receipt
   * posts against its chart account; omitted, the receipt stays outside treasury as before.
   */
  treasuryAccountId: RecordIdSchema.optional(),
  /** Omitted means oldest open installment first. */
  allocations: z
    .array(z.strictObject({ installmentId: RecordIdSchema, amount: MoneySchema }))
    .max(240)
    .optional(),
  notes: NoteSchema.optional(),
  /** A retried submission returns the original receipt instead of collecting the money twice. */
  idempotencyKey: z.string().min(8).max(200),
});
export type RecordReceipt = z.infer<typeof RecordReceiptSchema>;

/** The methods by which money is received directly into a treasury account (not through custody). */
export const DIRECT_RECEIPT_METHODS = ['cash', 'bankTransfer', 'card'] as const;
export type DirectReceiptMethod = (typeof DIRECT_RECEIPT_METHODS)[number];

/** The treasury account kinds a direct receipt by this method may land in. */
export const RECEIPT_ACCOUNT_KINDS: Readonly<Record<DirectReceiptMethod, readonly string[]>> = {
  cash: ['cashbox'],
  bankTransfer: ['bank'],
  card: ['bank'],
};

export function isDirectReceiptMethod(method: string): method is DirectReceiptMethod {
  return (DIRECT_RECEIPT_METHODS as readonly string[]).includes(method);
}

/**
 * A deposit received on a reservation: real money, through treasury, before any contract exists. It
 * is held as the reservation's unapplied funds and allocated to the contract's earliest rows once,
 * when the contract is activated — never collected, moved or posted again.
 */
export const RecordReservationReceiptSchema = z.strictObject({
  reservationId: RecordIdSchema,
  amount: MoneySchema,
  method: z.enum(DIRECT_RECEIPT_METHODS),
  receivedOn: BusinessDateSchema,
  treasuryAccountId: RecordIdSchema,
  depositReference: z.string().trim().max(120).optional(),
  transactionReference: z.string().trim().max(120).optional(),
  notes: NoteSchema.optional(),
  idempotencyKey: z.string().min(8).max(200),
});
export type RecordReservationReceipt = z.infer<typeof RecordReservationReceiptSchema>;

/** A reservation's money as received: what was agreed, what arrived, and what remains to apply. */
export const ReservationFundsSchema = z.strictObject({
  reservationId: RecordIdSchema,
  agreed: MoneySchema,
  /** Posted, unreversed deposit receipts. */
  received: MoneySchema,
  /** Of `received`, the part already allocated to the contract's instalments. */
  applied: MoneySchema,
  /** Of `received`, the part not yet applied. */
  unapplied: MoneySchema,
  receipts: z.array(
    z.strictObject({
      receiptId: RecordIdSchema,
      receiptNumber: z.string(),
      amount: MoneySchema,
      method: PaymentMethodSchema,
      receivedOn: BusinessDateSchema,
      state: ReceiptStateSchema,
      applied: z.boolean(),
      treasuryAccountId: RecordIdSchema.optional(),
    }),
  ),
  /**
   * Cheques and notes taken for this reservation (SALE-DEPOSIT-003). Not money until collected: a
   * collected one appears among the receipts; one still in custody counts for nothing.
   */
  instruments: z.array(
    z.strictObject({
      instrumentId: RecordIdSchema,
      kind: z.enum(['cheque', 'promissoryNote']),
      instrumentNumber: z.string(),
      amount: MoneySchema,
      dueOn: BusinessDateSchema,
      state: z.string(),
      contractId: RecordIdSchema.optional(),
    }),
  ),
});
export type ReservationFunds = z.infer<typeof ReservationFundsSchema>;

export const ReverseReceiptSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
});
export type ReverseReceipt = z.infer<typeof ReverseReceiptSchema>;

export const ReceiptQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
  contractId: RecordIdSchema.optional(),
  /** A reservation's deposit receipts. */
  reservationId: RecordIdSchema.optional(),
  customerId: RecordIdSchema.optional(),
  projectId: RecordIdSchema.optional(),
  method: PaymentMethodSchema.optional(),
  state: ReceiptStateSchema.optional(),
  /** Received on or after this day, in the organization's calendar (a daily or period collection). */
  receivedFrom: BusinessDateSchema.optional(),
  /** Received on or before this day. */
  receivedTo: BusinessDateSchema.optional(),
});
export type ReceiptQuery = z.infer<typeof ReceiptQuerySchema>;

export const ReceiptPageSchema = z.strictObject({
  items: z.array(ReceiptSchema),
  total: z.number().int().nonnegative(),
  /**
   * What the matching **posted** receipts add up to, per currency, summed exactly in the database —
   * reversed receipts excluded. The whole filtered set, not just this page.
   */
  postedTotals: z.array(MoneySchema),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type ReceiptPage = z.infer<typeof ReceiptPageSchema>;

/* ------------------------------------------------------------------ instrument */

export const INSTRUMENT_KINDS = ['cheque', 'promissoryNote'] as const;
export const InstrumentKindSchema = z.enum(INSTRUMENT_KINDS);
export type InstrumentKind = z.infer<typeof InstrumentKindSchema>;

/**
 * Custody states (`COL-CHECK-002`, `G-06`). `cleared` is "collected": the money is in and a receipt
 * records it. `inSafe` is paper held in the company's safe; `legal` is paper with the lawyers;
 * `returnedToCustomer` and `endorsed` are paper that left the company. `renewed` is a promissory note
 * replaced by a new note with a later maturity (`COL-NOTE-001`).
 */
export const INSTRUMENT_STATES = [
  'received',
  'inSafe',
  'deposited',
  'presented',
  'cleared',
  'returned',
  'replaced',
  'cancelled',
  'returnedToCustomer',
  'legal',
  'endorsed',
  'renewed',
] as const;
export const InstrumentStateSchema = z.enum(INSTRUMENT_STATES);
export type InstrumentState = z.infer<typeof InstrumentStateSchema>;

/**
 * Permitted moves.
 *
 * A cheque is **deposited**; a promissory note is **presented**. Either may come back unpaid
 * (`returned`) — even after it was collected, when the bank reverses the credit: then the receipt that
 * settled the instalment is reversed with it, because a bounced cheque is never treated as settled
 * (`COL-CHECK-003`). A returned instrument is replaced by a new one, renewed, handed back, or escalated
 * to legal — never edited, because the original paper is the evidence. Endorsement to a third party
 * exists only when the deployment allows it (`BD-52`); renewal only for promissory notes.
 */
export const INSTRUMENT_TRANSITIONS: Readonly<Record<InstrumentState, readonly InstrumentState[]>> =
  {
    received: [
      'inSafe',
      'deposited',
      'presented',
      'returned',
      'replaced',
      'cancelled',
      'returnedToCustomer',
      'endorsed',
      'renewed',
    ],
    inSafe: [
      'deposited',
      'presented',
      'replaced',
      'cancelled',
      'returnedToCustomer',
      'endorsed',
      'renewed',
    ],
    deposited: ['cleared', 'returned'],
    presented: ['cleared', 'returned'],
    returned: [
      'inSafe',
      'deposited',
      'presented',
      'replaced',
      'cancelled',
      'returnedToCustomer',
      'legal',
      'renewed',
    ],
    cleared: ['returned'],
    legal: ['cleared', 'returnedToCustomer', 'cancelled'],
    replaced: [],
    cancelled: [],
    returnedToCustomer: [],
    endorsed: [],
    renewed: [],
  };

export function canTransitionInstrument(from: InstrumentState, to: InstrumentState): boolean {
  return INSTRUMENT_TRANSITIONS[from].includes(to);
}

/** States in which the company still holds the paper or awaits its money. */
export const LIVE_INSTRUMENT_STATES = [
  'received',
  'inSafe',
  'deposited',
  'presented',
  'returned',
  'legal',
] as const satisfies readonly InstrumentState[];

/** Where a cheque's endorsement request stands (`BD-52`). `applied` = approved and endorsed. */
export const INSTRUMENT_ENDORSEMENT_STATUSES = [
  'pendingApproval',
  'applied',
  'rejected',
  'cancelled',
  'expired',
] as const;
export type InstrumentEndorsementStatus = (typeof INSTRUMENT_ENDORSEMENT_STATUSES)[number];

export const InstrumentSchema = z.strictObject({
  instrumentId: RecordIdSchema,
  kind: InstrumentKindSchema,
  /** The serial printed on the cheque or note, exactly as entered. Never reformatted. */
  instrumentNumber: z.string().trim().min(1).max(60),
  customerId: RecordIdSchema,
  /** The contract it pays. Absent while it is a reservation's deposit (SALE-DEPOSIT-003). */
  contractId: RecordIdSchema.optional(),
  /**
   * The reservation whose deposit it is, when taken before any contract exists. It counts as money
   * only once collected; at activation, a paper still in custody moves to the contract.
   */
  reservationId: RecordIdSchema.optional(),
  projectId: RecordIdSchema,
  /** The installment this instrument is meant to settle, when it is earmarked for one. */
  installmentId: RecordIdSchema.optional(),
  amount: MoneySchema,
  issuedOn: BusinessDateSchema,
  dueOn: BusinessDateSchema,
  /** Cheques only. Stored as entered; no bank registry is consulted. */
  bankName: z.string().trim().max(120).optional(),
  /** Who signed it. Often, but not always, the customer. */
  drawerName: EnteredNameSchema,
  state: InstrumentStateSchema,
  /** Where the physical paper is. A note, because custody of paper is a real operational problem. */
  custodyLocation: z.string().trim().max(200).optional(),
  /** Who holds the paper now: the recipient of the last acknowledged handover (`G-06`). */
  custodianAccountId: z.string().min(1).max(200).optional(),
  /** A handover minute issued and not yet acknowledged by its recipient. */
  pendingHandoverId: RecordIdSchema.optional(),
  /** The bank account the cheque was deposited to — a treasury account. */
  depositAccountId: RecordIdSchema.optional(),
  /** The receipt issued when it cleared. */
  settlementReceiptId: RecordIdSchema.optional(),
  replacedByInstrumentId: RecordIdSchema.optional(),
  renewedFromInstrumentId: RecordIdSchema.optional(),
  /** The third party a cheque was endorsed to, as entered (`BD-52`). */
  endorsedTo: z.string().max(160).optional(),
  /**
   * The cheque's endorsement request, safe to show: awaiting approval while one is open (the paper is
   * then frozen — no second request, no handover, no state change), otherwise the outcome of the last
   * one. No approval identifier is exposed. Absent when endorsement was never asked for.
   */
  endorsement: z
    .strictObject({
      status: z.enum(INSTRUMENT_ENDORSEMENT_STATUSES),
      endorsedTo: z.string().max(160),
      requestedAt: InstantSchema.optional(),
      decidedAt: InstantSchema.optional(),
    })
    .optional(),
  returnReason: z.string().max(500).optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  teamId: RecordIdSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Instrument = z.infer<typeof InstrumentSchema>;

export const CreateInstrumentSchema = z
  .strictObject({
    kind: InstrumentKindSchema,
    instrumentNumber: z.string().trim().min(1).max(60),
    /** The contract it pays — or, before one exists, the reservation whose deposit it is. */
    contractId: RecordIdSchema.optional(),
    reservationId: RecordIdSchema.optional(),
    installmentId: RecordIdSchema.optional(),
    amount: MoneySchema,
    issuedOn: BusinessDateSchema,
    dueOn: BusinessDateSchema,
    bankName: z.string().trim().max(120).optional(),
    drawerName: EnteredNameSchema,
    custodyLocation: z.string().trim().max(200).optional(),
    /** A retried submission returns the instrument already recorded. */
    idempotencyKey: z.string().trim().min(8).max(100).optional(),
  })
  .refine((value) => (value.contractId === undefined) !== (value.reservationId === undefined), {
    message: 'CONTRACT_OR_RESERVATION_REQUIRED',
    path: ['contractId'],
  });
export type CreateInstrument = z.infer<typeof CreateInstrumentSchema>;

export const ChangeInstrumentStateSchema = z.strictObject({
  state: InstrumentStateSchema,
  reason: z.string().trim().max(500).optional(),
  custodyLocation: z.string().trim().max(200).optional(),
  /** Required when moving to `replaced`. */
  replacedByInstrumentId: RecordIdSchema.optional(),
  /** The treasury bank account, when depositing or collecting. */
  depositAccountId: RecordIdSchema.optional(),
  /** The day the money was collected; required when moving to `cleared`. */
  collectedOn: BusinessDateSchema.optional(),
  /** Required when moving to `endorsed`. */
  endorsedTo: z.string().trim().min(2).max(160).optional(),
  expectedVersion: z.number().int().positive().optional(),
});
export type ChangeInstrumentState = z.infer<typeof ChangeInstrumentStateSchema>;

/** Renew a promissory note: a new note with a later maturity replaces it (`COL-NOTE-001`). */
export const RenewInstrumentSchema = z
  .strictObject({
    instrumentNumber: z.string().trim().min(1).max(60),
    issuedOn: BusinessDateSchema,
    dueOn: BusinessDateSchema,
    amount: MoneySchema.optional(),
    reason: z.string().trim().min(3).max(500),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => value.issuedOn <= value.dueOn, {
    message: 'DUE_BEFORE_ISSUED',
    path: ['dueOn'],
  });
export type RenewInstrument = z.infer<typeof RenewInstrumentSchema>;

/** One custody or state change, append-only: the instrument's history (`COL-CHECK-002`). */
export const InstrumentEventSchema = z.strictObject({
  eventId: RecordIdSchema,
  instrumentId: RecordIdSchema,
  kind: z.enum(['created', 'stateChanged', 'handoverIssued', 'handoverAcknowledged', 'renewed']),
  from: InstrumentStateSchema.optional(),
  to: InstrumentStateSchema.optional(),
  reason: z.string().optional(),
  actorAccountId: z.string(),
  handoverId: RecordIdSchema.optional(),
  receiptId: RecordIdSchema.optional(),
  occurredAt: InstantSchema,
});
export type InstrumentEvent = z.infer<typeof InstrumentEventSchema>;

export const InstrumentHistorySchema = z.strictObject({ items: z.array(InstrumentEventSchema) });

export const HANDOVER_STATES = ['pending', 'acknowledged', 'cancelled'] as const;
export const HandoverStateSchema = z.enum(HANDOVER_STATES);
export type HandoverState = z.infer<typeof HandoverStateSchema>;

/**
 * A numbered handover minute (`G-06`): who handed which papers to whom, where, and when the recipient
 * acknowledged them. Custody passes on acknowledgement — nobody answers for paper they have not
 * confirmed holding (`SD-07`, `BD-56`).
 */
export const HandoverSchema = z.strictObject({
  handoverId: RecordIdSchema,
  handoverNumber: z.string(),
  instrumentIds: z.array(RecordIdSchema),
  fromAccountId: z.string(),
  toAccountId: z.string(),
  location: z.string(),
  notes: z.string().optional(),
  state: HandoverStateSchema,
  acknowledgedAt: InstantSchema.optional(),
  cancelledAt: InstantSchema.optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  createdAt: InstantSchema,
});
export type Handover = z.infer<typeof HandoverSchema>;

export const CreateHandoverSchema = z.strictObject({
  instrumentIds: z.array(RecordIdSchema).min(1).max(50),
  toAccountId: z.string().min(1).max(200),
  location: z.string().trim().min(2).max(200),
  notes: z.string().trim().max(500).optional(),
  idempotencyKey: z.string().trim().min(8).max(100),
});
export type CreateHandover = z.infer<typeof CreateHandoverSchema>;

export const HandoverListSchema = z.strictObject({ items: z.array(HandoverSchema) });

/** The due calendar and ageing of instruments the company still holds (`COL-NOTE-002`). */
export const InstrumentDueQuerySchema = z
  .strictObject({
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    kind: InstrumentKindSchema.optional(),
  })
  .refine((value) => value.from <= value.to, { message: 'RANGE_REVERSED', path: ['to'] });
export type InstrumentDueQuery = z.infer<typeof InstrumentDueQuerySchema>;

export const INSTRUMENT_AGEING_BUCKETS = [
  'notDue',
  'days1to30',
  'days31to60',
  'days61to90',
  'over90',
] as const;

export const InstrumentDueReportSchema = z.strictObject({
  asOf: BusinessDateSchema,
  /** The currency of the ageing amounts; absent when nothing is held past due. */
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
  /** Live instruments falling due in the range, earliest first. */
  calendar: z.array(InstrumentSchema),
  /** Live instruments still held past their due date, by days overdue. */
  ageing: z.array(
    z.strictObject({
      bucket: z.enum(INSTRUMENT_AGEING_BUCKETS),
      count: z.number().int().nonnegative(),
      amount: DecimalStringSchema,
    }),
  ),
  truncated: z.boolean(),
});
export type InstrumentDueReport = z.infer<typeof InstrumentDueReportSchema>;

export const InstrumentQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
  kind: InstrumentKindSchema.optional(),
  state: InstrumentStateSchema.optional(),
  custodianAccountId: z.string().min(1).max(200).optional(),
  contractId: RecordIdSchema.optional(),
  customerId: RecordIdSchema.optional(),
  projectId: RecordIdSchema.optional(),
});
export type InstrumentQuery = z.infer<typeof InstrumentQuerySchema>;

export const InstrumentPageSchema = z.strictObject({
  items: z.array(InstrumentSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type InstrumentPage = z.infer<typeof InstrumentPageSchema>;

/* -------------------------------------------------------------------- reminder */

/**
 * The reminder window. Master Mapping's collection flow asks for a reminder before an installment
 * falls due; 15 days is the demonstration's window and is a configuration value, not a business rule
 * anyone has approved (`SD-07`).
 */
export const REMINDER_WINDOW_DAYS = 15;

export const REMINDER_CHANNELS = ['whatsapp', 'sms', 'email'] as const;
export const ReminderChannelSchema = z.enum(REMINDER_CHANNELS);
export type ReminderChannel = z.infer<typeof ReminderChannelSchema>;

/**
 * Reminder lifecycle.
 *
 * `simulated` is a deliberate state, not a synonym for `sent`. Nothing is connected: no WhatsApp
 * Business account, no approved template, no provider (ADR-0026). A reminder that a simulated adapter
 * "delivered" reached nobody, and calling that `sent` would be a lie the interface then repeats.
 * `sent` exists in the vocabulary so that connecting a real provider later adds a transition rather
 * than reinterpreting an existing one.
 */
export const REMINDER_STATES = ['scheduled', 'ready', 'simulated', 'sent', 'failed'] as const;
export const ReminderStateSchema = z.enum(REMINDER_STATES);
export type ReminderState = z.infer<typeof ReminderStateSchema>;

export const REMINDER_TRANSITIONS: Readonly<Record<ReminderState, readonly ReminderState[]>> = {
  scheduled: ['ready', 'failed'],
  ready: ['simulated', 'sent', 'failed'],
  simulated: ['ready'],
  sent: [],
  failed: ['ready'],
};

export function canTransitionReminder(from: ReminderState, to: ReminderState): boolean {
  return REMINDER_TRANSITIONS[from].includes(to);
}

export const ReminderSchema = z.strictObject({
  reminderId: RecordIdSchema,
  installmentId: RecordIdSchema,
  contractId: RecordIdSchema,
  customerId: RecordIdSchema,
  projectId: RecordIdSchema,
  /** The due date this reminder is about, and the key that makes generation idempotent. */
  dueOn: BusinessDateSchema,
  amount: MoneySchema,
  channel: ReminderChannelSchema,
  state: ReminderStateSchema,
  /** Both languages, always. The message is composed here so it can be previewed before anything. */
  messageAr: z.string().max(1000),
  messageEn: z.string().max(1000),
  generatedOn: BusinessDateSchema,
  lastAttemptAt: InstantSchema.optional(),
  failureReason: z.string().max(500).optional(),
  /** True whenever the state was produced by the simulated adapter rather than a provider. */
  simulated: z.boolean(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  teamId: RecordIdSchema.optional(),
  salesOwnerAccountId: z.string().min(1).max(200),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Reminder = z.infer<typeof ReminderSchema>;

export const GenerateRemindersSchema = z.strictObject({
  withinDays: z.number().int().min(1).max(90).default(REMINDER_WINDOW_DAYS),
  channel: ReminderChannelSchema.default('whatsapp'),
});
export type GenerateReminders = z.infer<typeof GenerateRemindersSchema>;

export const ReminderActionSchema = z.strictObject({
  state: z.enum(['ready', 'simulated', 'failed']),
  reason: z.string().trim().max(500).optional(),
});
export type ReminderAction = z.infer<typeof ReminderActionSchema>;

export const ReminderQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
  state: ReminderStateSchema.optional(),
  contractId: RecordIdSchema.optional(),
  customerId: RecordIdSchema.optional(),
  projectId: RecordIdSchema.optional(),
});
export type ReminderQuery = z.infer<typeof ReminderQuerySchema>;

export const ReminderPageSchema = z.strictObject({
  items: z.array(ReminderSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type ReminderPage = z.infer<typeof ReminderPageSchema>;

export const GenerateRemindersResultSchema = z.strictObject({
  created: z.number().int().nonnegative(),
  existing: z.number().int().nonnegative(),
  /** Stated on every response so a caller cannot mistake this for a delivery integration. */
  deliveryConnected: z.literal(false),
});
export type GenerateRemindersResult = z.infer<typeof GenerateRemindersResultSchema>;

export const COLLECTION_AUDIT_ACTIONS = {
  receiptRecorded: 'collection.receipt.recorded',
  /** A receipt received before the cutover, onboarded from the client's records (BD-63). */
  receiptOnboarded: 'collection.receipt.onboarded',
  receiptReversed: 'collection.receipt.reversed',
  /** A reservation deposit allocated to its contract's instalments at activation (no new money). */
  receiptApplied: 'collection.receipt.appliedToContract',
  receiptRefused: 'collection.receipt.refused',
  instrumentCreated: 'collection.instrument.created',
  instrumentStateChanged: 'collection.instrument.stateChanged',
  instrumentRefused: 'collection.instrument.refused',
  remindersGenerated: 'collection.reminder.generated',
  reminderStateChanged: 'collection.reminder.stateChanged',
  handoverIssued: 'collection.instrument.handoverIssued',
  handoverAcknowledged: 'collection.instrument.handoverAcknowledged',
  handoverCancelled: 'collection.instrument.handoverCancelled',
  instrumentRenewed: 'collection.instrument.renewed',
  instrumentsDueNotified: 'collection.instrument.dueNotified',
} as const;

/** Endorsing a received cheque to a third party is governed only where a policy names it (`BD-52`). */
export const INSTRUMENT_APPROVAL_OPERATIONS = { endorse: 'collection.instrument.endorse' } as const;
